import React, {useState, useMemo, useEffect, useRef} from 'react';
import {Tabs} from 'antd';
import styled from 'styled-components';
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {useIntl} from "react-intl";
import {OneWayForm} from "./components/OneWayForm";
import {RoundTripForm} from "./components/RoundTripForm";
import {MultiLegForm} from "./components/MultiLegForm";
import {FlightRequestConfirmationModal} from "./components/FlightRequestConfirmationModal";
import {FoundFlightsSection} from "./components/FoundFlightsSection";
import {CustomDrawer} from "../../../../components/UI/AntCustom/CustomDrawer";
import {PlanePhotos} from "../../../../pages/Lk/OperatorsOnly/Fleet/components/PlanePhotos";
import {FlightDrawerPlane} from "../../../../components/businessCommon/flightDrawer/plane/FlightDrawerPlane";
import {FlightDrawerPrice} from "../../../../components/businessCommon/flightDrawer/price/FlightDrawerPrice";
import {FlightDrawerContentWrapper, FlightDrawerPhotosAndContentWrapper} from "../../../../components/businessCommon/flightDrawer/wrappers/FlightDrawerStyledComponents";
import {IPlane} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {IFlight} from "./components/FoundFlightsSection";
import {useAppSelector, useAppDispatch} from "../../../../store/store";
import {IBrokerAircraft} from "../../../../store/reducers/newRequestReducer/newRequestReducerTypes";
import {Logger} from "../../../../utils/logger/Logger";
import {Loader} from "../../../../components/UI/Loader/Loader";
import {clearBrokerAircraftSearch} from "../../../../store/reducers/newRequestReducer/newRequestReducer";
import {CustomResult} from "../../../../components/UI/AntCustom/CustomResult";
import {useCurrencyConverter} from "../../../../hooks/useCurrencyConverter";
import {parseStringAsUTCDate, isValidDate, formatDate} from "../../../../utils/helpers/dateHelpers/dateHelpers";
import {getBaseCurrencyId, getBaseCurrencyInfo} from "../../../../utils/helpers/currencyConverter/currencyConverter";
import {getAirportsFromStoreByIds, fetchInitialAirportForSelect} from "../../../../utils/helpers/selectDataFetchers/fetchAirports";
import {API} from "../../../../API/API";
import {AirportApiGetAirportRequest} from "../../../../proto/generated/api_entities_pb";
import {mapAirportEntityToIAirport} from "../../../../store/reducers/fleetReducer/fleetMappers";
import {DisplayAirport} from "../../../../utils/helpers/textDisplayHelpers/DisplayAirport";
import {NewTripRequestPageMessages} from "./NewTripRequestPageMessages";
import {amplitudeEvents} from "../../../../utils/analytics/amplitude";



enum NewTripRequestTabKeysEnum {
  ONE_WAY = 'one_way',
  ROUND_TRIP = 'round_trip',
  MULTI_LEG = 'multi_leg',
}

const ScrollableContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 16px;
  overflow-y: auto;
`;

export const NewTripRequestPage = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [activeTabKey, setActiveTabKey] = useState<NewTripRequestTabKeysEnum>(NewTripRequestTabKeysEnum.ONE_WAY);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [selectedFlights, setSelectedFlights] = useState<Set<string>>(new Set());
  const [selectedFlightForDetails, setSelectedFlightForDetails] = useState<IFlight | null>(null);
  const [isFlightDetailsDrawerOpen, setIsFlightDetailsDrawerOpen] = useState(false);
  const [formClearTrigger, setFormClearTrigger] = useState(0);
  // Store fetched airport info for multileg routes (airports not in store)
  const [fetchedAirports, setFetchedAirports] = useState<Record<number, { code: string; location_and_name: string }>>({});
  const resultsRef = useRef<HTMLDivElement>(null);
  const wasLoadingRef = useRef(false);

  // Получаем данные из reducer
  const { items: aircraftList, isLoading: isSearchLoading, totalCount } = useAppSelector(state => state.newRequest.brokerAircraftSearch);
  const lastSearchParams = useAppSelector(state => state.newRequest.lastSearchParams);
  const lastSearchPayload = useAppSelector(state => state.newRequest.lastSearchPayload);
  const userCurrency = useAppSelector(state => state.user.oneItemObject.item?.currency);
  const companyCurrency = useAppSelector(state => state.company.oneItemObject.item?.currency);
  
  // Fetch missing airports from API when multileg routes change
  useEffect(() => {
    if (lastSearchParams?.multilegRoutes && lastSearchParams.multilegRoutes.length > 0) {
      const airportIds = new Set<number>();
      lastSearchParams.multilegRoutes.forEach(leg => {
        airportIds.add(leg.airportFromId);
        airportIds.add(leg.airportToId);
      });
      
      // Get airports from store
      const airportOptions = getAirportsFromStoreByIds(Array.from(airportIds));
      const foundIds = new Set(airportOptions.map(a => a.value as number));
      const missingIds = Array.from(airportIds).filter(id => !foundIds.has(id));
      
      // Also check if airports are in lastSearchParams (origin/destination)
      const checkIds = missingIds.filter(id => 
        id !== lastSearchParams.airportFromId && 
        id !== lastSearchParams.airportToId
      );
      
      // Fetch missing airports from API
      if (checkIds.length > 0) {
        const fetchAirports = async () => {
          const newFetchedAirports: Record<number, { code: string; location_and_name: string }> = {};
          
          await Promise.all(
            checkIds.map(async (airportId) => {
              try {
                const req = new AirportApiGetAirportRequest().setAirportid(airportId);
                const airportEntity = (await API.getAirportById(req)).getAirport();
                if (airportEntity) {
                  const airport = mapAirportEntityToIAirport(airportEntity);
                  newFetchedAirports[airportId] = {
                    code: airport.icaoCode || airport.iataCode || '',
                    location_and_name: DisplayAirport(airport, 'location_and_name'),
                  };
                }
              } catch (error) {
                console.error(`Failed to load airport ${airportId}:`, error);
              }
            })
          );
          
          if (Object.keys(newFetchedAirports).length > 0) {
            setFetchedAirports(prev => ({ ...prev, ...newFetchedAirports }));
          }
        };
        
        fetchAirports();
      }
    } else {
      // Clear fetched airports when not using multileg routes
      setFetchedAirports({});
    }
  }, [lastSearchParams?.multilegRoutes]);
  
  // Используем валюту пользователя, если она есть, иначе валюту компании
  const targetCurrency = userCurrency || companyCurrency;
  
  // Хук для конвертации валют
  const { convert, convertSync, isReady } = useCurrencyConverter();
  
  // Получаем ID базовой валюты
  const baseCurrencyId = useMemo(() => {
    if (isReady) {
      return getBaseCurrencyId();
    }
    return null;
  }, [isReady]);
  
  // Получаем информацию о базовой валюте (ID и название)
  const baseCurrency = useMemo(() => {
    if (isReady) {
      return getBaseCurrencyInfo();
    }
    return null;
  }, [isReady]);

  // Scroll to results and track Search Flight in Amplitude when search finishes
  useEffect(() => {
    if (isSearchLoading) {
      wasLoadingRef.current = true;
    } else if (wasLoadingRef.current && lastSearchParams) {
      wasLoadingRef.current = false;
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const prices = aircraftList.map((a) => a.preliminaryPrice).filter((p) => p > 0);
      const minPrice = prices.length ? Math.min(...prices) : undefined;
      const maxPrice = prices.length ? Math.max(...prices) : undefined;
      const p = lastSearchPayload;
      const filters: Record<string, unknown> = {};
      if (p?.planeCategoryIds?.length) filters.filter_plane_category_ids = p.planeCategoryIds;
      if (p?.planeModel) filters.filter_plane_model = p.planeModel;
      if (p?.manufactureYearFrom != null) filters.filter_manufacture_year_from = p.manufactureYearFrom;
      if (p?.manufactureYearTo != null) filters.filter_manufacture_year_to = p.manufactureYearTo;
      if (p?.renovationYearFrom != null) filters.filter_renovation_year_from = p.renovationYearFrom;
      if (p?.renovationYearTo != null) filters.filter_renovation_year_to = p.renovationYearTo;
      if (p?.positioningDurationFromHours != null) filters.filter_positioning_duration_from_hours = p.positioningDurationFromHours;
      if (p?.positioningDurationToHours != null) filters.filter_positioning_duration_to_hours = p.positioningDurationToHours;
      if (p?.landingCountFrom != null) filters.filter_landing_count_from = p.landingCountFrom;
      if (p?.landingCountTo != null) filters.filter_landing_count_to = p.landingCountTo;
      if (p?.refuelingCountFrom != null) filters.filter_refueling_count_from = p.refuelingCountFrom;
      if (p?.refuelingCountTo != null) filters.filter_refueling_count_to = p.refuelingCountTo;
      if (p?.updatedAtFrom) filters.filter_updated_at_from = p.updatedAtFrom;
      if (p?.amenities?.length) filters.filter_amenities = p.amenities;
      if (p?.operatorIds?.length) filters.filter_operator_ids = p.operatorIds;
      if (p?.planeIds?.length) filters.filter_plane_ids = p.planeIds;
      amplitudeEvents.searchFlight({
        airport_from_id: lastSearchParams.airportFromId,
        airport_to_id: lastSearchParams.airportToId,
        airport_from_code: lastSearchParams.airportFrom?.code,
        airport_to_code: lastSearchParams.airportTo?.code,
        departure_date: lastSearchParams.departureDate?.toISOString?.(),
        pax: lastSearchParams.pax,
        pax_to: lastSearchParams.paxTo,
        multileg_legs_count: lastSearchParams.multilegRoutes?.length,
        aircraft_count: totalCount,
        aircraft_found: aircraftList.length,
        min_price: minPrice,
        max_price: maxPrice,
        ...filters,
      });
    }
  }, [isSearchLoading, lastSearchParams, lastSearchPayload, aircraftList, totalCount]);

  // Логируем данные из reducer для отладки
  useEffect(() => {
    Logger.collapsedLog('[NewTripRequestPage] Aircraft data from reducer', {
      aircraftListLength: aircraftList.length,
      totalCount,
      isSearchLoading,
      aircraftList: aircraftList.map(a => ({
        planeId: a.planeId,
        registrationNumber: a.registrationNumber,
        operatorName: a.operatorName,
      })),
    }, 'blue');
  }, [aircraftList, totalCount, isSearchLoading]);

  // Преобразуем данные из reducer в формат IFlight
  const flights = useMemo(() => {
    // Route destination code for "Arrival time" (must not use plane's base airport)
    const getDestinationAirportCode = (): string => {
      if (lastSearchParams?.airportTo?.code) return lastSearchParams.airportTo.code;
      const multileg = lastSearchParams?.multilegRoutes;
      if (!multileg?.length) return '';
      const last = multileg[multileg.length - 1];
      const id = last.airportToId;
      if (lastSearchParams.airportFromId === id && lastSearchParams.airportFrom?.code) return lastSearchParams.airportFrom.code;
      if (lastSearchParams.airportToId === id && lastSearchParams.airportTo?.code) return lastSearchParams.airportTo.code;
      if (fetchedAirports[id]?.code) return fetchedAirports[id].code;
      const opts = getAirportsFromStoreByIds([id]);
      const o = opts[0];
      if (o?.label) {
        const m = o.label.match(/\b([A-Z]{4})\b/) || o.label.match(/\b([A-Z]{3})\b/);
        return m ? m[1] : '';
      }
      return '';
    };

    return aircraftList.map((aircraft: IBrokerAircraft): IFlight => {
      // Парсим даты доступности
      let availabilityStartDate: Date = lastSearchParams?.departureDate || new Date();
      let availabilityEndDate: Date = lastSearchParams?.departureDate || new Date();
      
      if (aircraft.availableFromDate) {
        try {
          const parsedDate = parseStringAsUTCDate(aircraft.availableFromDate);
          if (isValidDate(parsedDate)) {
            availabilityStartDate = parsedDate;
          }
        } catch (e) {
          // Если не удалось распарсить, используем дату из поиска
        }
      }
      
      if (aircraft.availableToDate && aircraft.availableToDate.trim() !== '') {
        try {
          const parsedDate = parseStringAsUTCDate(aircraft.availableToDate);
          if (isValidDate(parsedDate)) {
            availabilityEndDate = parsedDate;
          }
        } catch (e) {
          // Если не удалось распарсить, используем дату из поиска
        }
      }
      
      // Если availableToDate пустая или не установлена, но есть routeErrorMessage,
      // устанавливаем дату окончания больше даты начала (например, +1 день)
      // Это нужно, чтобы показать, что самолет доступен хотя бы на некоторое время
      if ((!aircraft.availableToDate || aircraft.availableToDate.trim() === '') && aircraft.routeErrorMessage) {
        availabilityEndDate = new Date(availabilityStartDate.getTime() + 24 * 60 * 60 * 1000);
      } else if (!aircraft.availableToDate || aircraft.availableToDate.trim() === '') {
        // Если availableToDate пустая, но нет routeErrorMessage, 
        // самолет доступен бесконечно - устанавливаем дату окончания далеко в будущем
        availabilityEndDate = new Date(availabilityStartDate.getTime() + 365 * 24 * 60 * 60 * 1000); // +1 год
      }
      
      // Убеждаемся, что дата окончания больше даты начала
      if (availabilityEndDate.getTime() <= availabilityStartDate.getTime()) {
        availabilityEndDate = new Date(availabilityStartDate.getTime() + 24 * 60 * 60 * 1000);
      }

      // Проверяем наличие ошибки маршрута
      const hasRouteError = !!aircraft.routeErrorMessage;
      const hasPriceError = aircraft.preliminaryPrice === 0 && hasRouteError;

      // Конвертируем цену из базовой валюты в валюту пользователя
      let convertedPrice = aircraft.preliminaryPrice;
      let displayCurrency = { id: aircraft.currencyId, name: aircraft.currencyName || '' };
      let priceInUserCurrency: number | undefined = undefined;
      let userCurrency: { id: number; name: string } | undefined = undefined;
      
      if (targetCurrency && targetCurrency.id && aircraft.preliminaryPrice > 0 && isReady && baseCurrencyId) {
        try {
          // preliminaryPrice приходит в базовой валюте, поэтому конвертируем из базовой валюты
          const originalPrice = aircraft.preliminaryPrice;
          
          // Используем базовую валюту для конвертации
          const fromCurrencyId = baseCurrencyId;
          
          // Используем синхронную версию, если кэш загружен
          convertedPrice = convertSync(
            originalPrice,
            fromCurrencyId, // Базовая валюта (откуда)
            targetCurrency.id   // Валюта пользователя (куда)
          );
          
          // Округляем конвертированную цену до 2 знаков после запятой
          priceInUserCurrency = Math.round(convertedPrice * 100) / 100;
          userCurrency = { id: targetCurrency.id, name: targetCurrency.name || '' };
          displayCurrency = userCurrency;
        } catch (error) {
          // Если конвертация не удалась, используем исходную цену
          console.error(`[NewTripRequestPage] Failed to convert currency:`, error);
        }
      } else {
        // Логируем, почему конвертация не произошла
        if (!targetCurrency || !targetCurrency.id) {
          Logger.collapsedLog('[NewTripRequestPage] Currency conversion skipped: no target currency', {}, 'yellow');
        } else if (!isReady) {
          Logger.collapsedLog('[NewTripRequestPage] Currency conversion skipped: converter not ready', {}, 'yellow');
        } else if (!baseCurrencyId) {
          Logger.collapsedLog('[NewTripRequestPage] Currency conversion skipped: base currency not found', {}, 'yellow');
        } else if (aircraft.preliminaryPrice <= 0) {
          Logger.collapsedLog('[NewTripRequestPage] Currency conversion skipped: price is 0 or negative', {}, 'yellow');
        }
      }

      return {
        id: String(aircraft.planeId),
        planeName: aircraft.planeTypeName || aircraft.registrationNumber,
        registrationNumber: aircraft.registrationNumber,
        companyName: aircraft.operatorName,
        price: convertedPrice,
        preliminaryPrice: aircraft.preliminaryPrice, // Оригинальная цена в базовой валюте
        // Важно: currency должен быть базовой валютой (не валютой самолета), так как preliminaryPrice в базовой валюте
        // Это нужно для правильного отображения оригинальной цены в скобках
        currency: baseCurrency || { id: aircraft.currencyId, name: aircraft.currencyName || '' },
        planeCurrency: aircraft.planeCurrencyId && aircraft.planeCurrencyName
          ? { id: aircraft.planeCurrencyId, name: aircraft.planeCurrencyName }
          : undefined,
        // Устанавливаем конвертированную цену и валюту пользователя для отображения
        priceInUserCurrency: priceInUserCurrency,
        userCurrency: userCurrency,
        routeFrom: {
          code: lastSearchParams?.airportFrom?.code || '',
          location_and_name: lastSearchParams?.airportFrom?.location_and_name || '',
        },
        routeTo: {
          code: lastSearchParams?.airportTo?.code || '',
          location_and_name: lastSearchParams?.airportTo?.location_and_name || '',
        },
        // Build itinerary: if multilegRoutes exist, we'd need to fetch airport info for each leg
        // For now, use routeFrom and routeTo, and if multilegRoutes exist, we'll build a basic itinerary
        itinerary: (() => {
          if (lastSearchParams?.multilegRoutes && lastSearchParams.multilegRoutes.length > 0) {
            const airportIds = new Set<number>();
            lastSearchParams.multilegRoutes.forEach(leg => {
              airportIds.add(leg.airportFromId);
              airportIds.add(leg.airportToId);
            });
            const airportOptions = getAirportsFromStoreByIds(Array.from(airportIds));
            
            const getAirportInfo = (airportId: number): { code: string; location_and_name: string } | null => {
              // First check if it's the origin or destination from search params
              if (airportId === lastSearchParams.airportFromId && lastSearchParams.airportFrom) {
                return { code: lastSearchParams.airportFrom.code || '', location_and_name: lastSearchParams.airportFrom.location_and_name || '' };
              }
              if (airportId === lastSearchParams.airportToId && lastSearchParams.airportTo) {
                return { code: lastSearchParams.airportTo.code || '', location_and_name: lastSearchParams.airportTo.location_and_name || '' };
              }
              
              // Check if we fetched this airport from API
              if (fetchedAirports[airportId]) {
                return fetchedAirports[airportId];
              }
              
              // Check store
              const airportOption = airportOptions.find(a => a.value === airportId);
              if (!airportOption?.label) return null;
              
              const parts = airportOption.label.split(', ').filter(p => p.trim());
              const icaoPart = parts.find(p => /^[A-Z]{4}$/.test(p.trim()));
              const iataPart = !icaoPart && parts.find(p => /^[A-Z]{3}$/.test(p.trim()));
              const codePart = icaoPart || iataPart;
              
              if (codePart) {
                const codeIndex = parts.indexOf(codePart);
                return { code: codePart.trim(), location_and_name: parts.slice(codeIndex + 1).join(', ') || codePart };
              }
              
              return { code: parts[0]?.substring(0, 4).toUpperCase().padEnd(4, 'X') || '', location_and_name: airportOption.label };
            };
            
            const itinerary: Array<{ code: string; location_and_name: string }> = [];
            const firstAirport = getAirportInfo(lastSearchParams.multilegRoutes[0].airportFromId);
            if (firstAirport) {
              itinerary.push(firstAirport);
            } else {
              // Fallback: show airport ID if info not found
              itinerary.push({
                code: `ID:${lastSearchParams.multilegRoutes[0].airportFromId}`,
                location_and_name: `Airport ${lastSearchParams.multilegRoutes[0].airportFromId}`,
              });
            }
            
            lastSearchParams.multilegRoutes.forEach(leg => {
              const dest = getAirportInfo(leg.airportToId);
              if (dest) {
                const last = itinerary[itinerary.length - 1];
                // Only add if it's different from the last airport (avoid duplicates for round trips)
                if (!last || last.code !== dest.code || last.location_and_name !== dest.location_and_name) {
                  itinerary.push(dest);
                }
              } else {
                // Fallback: show airport ID if info not found
                const last = itinerary[itinerary.length - 1];
                const fallbackAirport = {
                  code: `ID:${leg.airportToId}`,
                  location_and_name: `Airport ${leg.airportToId}`,
                };
                // Only add if it's different from the last airport
                if (!last || last.code !== fallbackAirport.code) {
                  itinerary.push(fallbackAirport);
                }
              }
            });
            
            return itinerary.length > 0 ? itinerary : undefined;
          }
          // For simple routes, build itinerary from routeFrom and routeTo
          // For round trips (when paxTo exists), add return to origin
          if (lastSearchParams?.airportFrom && lastSearchParams?.airportTo) {
            const itinerary = [
              {
                code: lastSearchParams.airportFrom.code || '',
                location_and_name: lastSearchParams.airportFrom.location_and_name || '',
              },
              {
                code: lastSearchParams.airportTo.code || '',
                location_and_name: lastSearchParams.airportTo.location_and_name || '',
              },
            ];
            
            // If it's a round trip (paxTo exists), add return to origin
            if (lastSearchParams.paxTo !== undefined && lastSearchParams.paxTo > 0) {
              itinerary.push({
                code: lastSearchParams.airportFrom.code || '',
                location_and_name: lastSearchParams.airportFrom.location_and_name || '',
              });
            }
            
            return itinerary;
          }
          return undefined;
        })(),
        availabilityStartDate,
        availabilityEndDate,
        timezoneOffset: lastSearchParams?.timezoneOffset || 0,
        pax: aircraft.maxPassengers,
        // Use arrival date calculated by backend (last leg departure + last leg flight duration)
        arrivalDate: (() => {
          if (aircraft.arrivalDate) {
            try {
              const parsed = parseStringAsUTCDate(aircraft.arrivalDate);
              if (isValidDate(parsed)) {
                return parsed;
              }
            } catch (e) {
              // Ignore parsing errors, fall through to return undefined
            }
          }
          return undefined;
        })(),
        adjustedDepartureDateTime: aircraft.adjustedDepartureDateTime,
        flightDurationMinutes: aircraft.flightDurationMinutes,
        lastLegFlightDurationMinutes: aircraft.lastLegFlightDurationMinutes,
        arrivalAirport: getDestinationAirportCode(),
        baseAirport: aircraft.baseAirportName || `${aircraft.baseAirportIcao || aircraft.baseAirportIata || ''}`,
        aircraftCategory: aircraft.planeCategoryName,
        planeImageFiles: [],
        isSelected: false,
        hasRouteWarning: hasRouteError,
        hasArrivalWarning: false,
        routeErrorMessage: aircraft.routeErrorMessage,
        hasPriceError,
        positioningDurationMinutes: aircraft.positioningDurationMinutes,
        positioningTechnicalStopCount: aircraft.positioningTechnicalStopCount,
        positioningFromAirportId: aircraft.positioningFromAirportId,
      };
    });
  }, [aircraftList, lastSearchParams, targetCurrency, isReady, convertSync, baseCurrencyId, baseCurrency, fetchedAirports]);

  // Преобразуем IFlight в IPlane для FlightDrawerPlane
  const convertFlightToPlane = (flight: IFlight | null): IPlane | null => {
    if (!flight) return null;

    // Создаем минимальный объект IPlane с тестовыми данными
    return {
      id: 0, // Тестовое значение
      registrationNumber: "N/A", // Тестовое значение
      planeType: {
        id: 0,
        name: flight.planeName,
        planeCategory: {
          id: 0,
          name: flight.aircraftCategory || "Unknown"
        }
      },
      baseAirport: {
        id: 0,
        name: flight.baseAirport,
        icaoCode: "",
        iataCode: "",
        countryName: "",
        timezone: {
          id: 0,
          name: "",
          offset: flight.timezoneOffset
        }
      },
      manufactureDate: new Date(2000, 0, 1), // Тестовое значение
      renovationDate: undefined,
      maxPassengers: flight.pax,
      maxDistance: 0, // Тестовое значение
      isAnimals: false,
      isCargo: false,
      isSmoking: false,
      isAmbulance: false,
      isWifi: flight.amenities?.wifi ?? false,
      isOvenAboard: flight.amenities?.ovenAboard ?? false,
      description: undefined,
      imageFiles: flight.planeImageFiles || [],
      insuranceFile: {} as any, // Тестовое значение
      certificateFile: {} as any, // Тестовое значение
      etopsFile: undefined,
      flyHourPrice: 0,
      flyHourCurrency: flight.currency,
      calculatorCategories: [],
      planeStatusId: 1
    };
  };

  // Функция очистки после успешной отправки запроса
  const handleRequestSubmitted = () => {
    // Очищаем результаты поиска
    dispatch(clearBrokerAircraftSearch());
    
    // Очищаем выбранные самолеты
    setSelectedFlights(new Set());
    
    // Очищаем поля формы (через trigger, который вызовет reset в формах)
    setFormClearTrigger(prev => prev + 1);
  };

  const tabList = [
    {
      key: NewTripRequestTabKeysEnum.ONE_WAY,
      label: intl.formatMessage(NewTripRequestPageMessages.oneWay),
      children: <OneWayForm clearTrigger={formClearTrigger} />
    },
    {
      key: NewTripRequestTabKeysEnum.ROUND_TRIP,
      label: intl.formatMessage(NewTripRequestPageMessages.roundTrip),
      children: <RoundTripForm clearTrigger={formClearTrigger} />
    },
    {
      key: NewTripRequestTabKeysEnum.MULTI_LEG,
      label: intl.formatMessage(NewTripRequestPageMessages.multiLeg),
      children: <MultiLegForm clearTrigger={formClearTrigger} />
    },
  ];

  return (
    <>
      <PageWrapper alignItems={"center"}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', alignItems: 'center', gap: 16 }}>
          <ScrollableContentContainer>
            <CustomCard title={intl.formatMessage(NewTripRequestPageMessages.title)} width={'max'} isContainerCard={true} onTabChange={(key: string) => {
              setActiveTabKey(key as NewTripRequestTabKeysEnum)
            }}>
              <Tabs
                activeKey={activeTabKey}
                onChange={(key: string) => setActiveTabKey(key as NewTripRequestTabKeysEnum)}
                items={tabList.map(t => ({ key: t.key, label: t.label, children: t.children }))}
              />
            </CustomCard>
            {/* Показываем лоадер во время поиска */}
            {isSearchLoading && (
              <div ref={resultsRef} style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Loader />
              </div>
            )}
            {/* Показываем блок с найденными самолетами только после получения ответа с бекенда */}
            {!isSearchLoading && (totalCount > 0 || aircraftList.length > 0) && (
              <div ref={resultsRef}>
              <FoundFlightsSection 
                flights={flights}
                selectedFlights={selectedFlights}
                setSelectedFlights={setSelectedFlights}
                onFlightNameClick={(flight: IFlight) => {
                  setSelectedFlightForDetails(flight);
                  setIsFlightDetailsDrawerOpen(true);
                }}
                onCreateRequest={() => {
                  const selected = flights.filter((f) => selectedFlights.has(f.id));
                  amplitudeEvents.enquireCharter({
                    planes_selected_count: selected.length,
                    plane_numbers: selected.map((f) => f.registrationNumber ?? f.planeName),
                    prices: selected.map((f) => f.price),
                    positioning_minutes: selected.map((f) => f.positioningDurationMinutes),
                    flight_time_minutes: selected.map((f) => f.flightDurationMinutes),
                  });
                  setIsConfirmationModalVisible(true);
                }}
              />
              </div>
            )}
            {/* Показываем сообщение "No flights found" если поиск завершен и ничего не найдено */}
            {!isSearchLoading && totalCount === 0 && aircraftList.length === 0 && lastSearchParams && (
              <div ref={resultsRef} style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <CustomResult title={intl.formatMessage(NewTripRequestPageMessages.noFlightsFound)} />
              </div>
            )}
          </ScrollableContentContainer>
        </div>
      </PageWrapper>
      <FlightRequestConfirmationModal
        isModalVisible={isConfirmationModalVisible}
        setIsModalVisible={setIsConfirmationModalVisible}
        searchParams={{
          ...lastSearchParams,
          paxTo: lastSearchParams?.paxTo,
          multilegRoutes: lastSearchParams?.multilegRoutes,
        }}
        selectedFlightsCount={selectedFlights.size}
        selectedFlightIds={selectedFlights}
        onRequestSubmitted={handleRequestSubmitted}
      />
      {selectedFlightForDetails && (
        <CustomDrawer
          open={isFlightDetailsDrawerOpen}
          onClose={() => setIsFlightDetailsDrawerOpen(false)}
          placement="right"
          width={600}
        >
          <FlightDrawerPhotosAndContentWrapper>
            {selectedFlightForDetails.planeImageFiles && selectedFlightForDetails.planeImageFiles.length > 0 && (
              <PlanePhotos imageFiles={selectedFlightForDetails.planeImageFiles} />
            )}

            <FlightDrawerContentWrapper>
              {convertFlightToPlane(selectedFlightForDetails) && (
                <FlightDrawerPlane plane={convertFlightToPlane(selectedFlightForDetails)!} />
              )}
              <FlightDrawerPrice price={selectedFlightForDetails.price} currency={selectedFlightForDetails.currency} />
            </FlightDrawerContentWrapper>
          </FlightDrawerPhotosAndContentWrapper>
        </CustomDrawer>
      )}
    </>
  );
};
